import { useState, useMemo, useEffect } from 'react';
import { useParticipantEPR } from '../ParticipantEPRContext';
import { useSession } from '../SessionContext';
import dayjs from 'dayjs';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Chip,
  Stack,
  Skeleton,
  Button,
  Modal,
  Autocomplete,
  TextField
} from '@mui/material';
import ImageOutlinedIcon from '@mui/icons-material/ImageOutlined';
import { supabase } from '../../supabaseClient';

const ACCENT = 'rgb(219, 234, 254)';
const ACCENT_HOVER = '#EFF6FF';
const ACCENT_SELECTED = '#BFDBFE';
const ACCENT_BORDER = '#60A5FA';
const TOPBAR_BG = '#dbeafe';

const imagingOptions = {
  xray: [
    'Chest',
    'Abdomen',
    'Left arm',
    'Right arm',
    'Left leg',
    'Right leg',
    'Pelvis',
  ],
  ultrasound: [
    'Abdomen',
    'Renal tract',
    'Lower limb Doppler',
    'Upper limb Doppler',
    'Hip',
    'Head',
  ],
  ct: [
    'Head',
    'Chest',
    'Abdomen/Pelvis',
    'Pulmonary angiogram',
  ],
  mri: [
    'Brain',
    'Spine',
    'Knee',
    'Abdomen',
  ],
  ecg: [
    '12-lead ECG',
    'Rhythm strip',
  ],
};

function buildPublicUrl(bucket, filePath) {
  const { data } = supabase.storage.from(bucket).getPublicUrl(filePath);
  return data.publicUrl;
}

function getImagingDate(item) {
  return item.timestamp || item.date || item.visible_time || item.created_at || null;
};

export default function RadiologyViewer() {
  const API_BASE = process.env.REACT_APP_API_URL;
  const { socket, room, liveId } = useSession();
  const [selectedImage, setSelectedImage] = useState(null);
  const { eprData, loading } = useParticipantEPR();
  const imaging = eprData?.eprImaging || [];

  const [requestOpen, setRequestOpen] = useState(false);
  const [savingReq, setSavingReq] = useState(false);

  //Request options
  const [modality, setModality] = useState(null);
  const [studyType, setStudyType] = useState(null);
  const modalityOptions = Object.keys(imagingOptions);
  const studyTypeOptions = modality ? imagingOptions[modality] || [] : [];

  const hydratedImaging = useMemo(() => {
    return imaging.map((item) => ({
      ...item,
      imageUrl:
        item.imageUrl || (item.bucket && item.file_path
          ? buildPublicUrl(item.bucket, item.file_path)
          : null),
    }));
  }, [imaging]);

  const sortedImaging = useMemo(() => {
    return [...hydratedImaging].sort((a, b) => {
      const ta = dayjs(getImagingDate(a)).valueOf() || 0;
      const tb = dayjs(getImagingDate(b)).valueOf() || 0;
      return tb - ta;
    });
  }, [hydratedImaging]);

  useEffect(() => {
    if (!selectedImage && sortedImaging.length) {
      setSelectedImage(sortedImaging[0]);
    }
  }, [sortedImaging, selectedImage]);

  useEffect(() => {
    if (!selectedImage) return;
    const stillThere = sortedImaging.find(
      (img) =>
        (img.image_id && img.image_id === selectedImage.image_id) ||
        (img.id && img.id === selectedImage.id)
    );
    if (!stillThere && sortedImaging.length) {
      setSelectedImage(sortedImaging[0]);
    }
  }, [sortedImaging, selectedImage]);

  const sendRequest = async() => {
      if (!liveId || !room || !modality) return;
      const requestmsg = studyType + ' (' + modality + ')';
    try {
      setSavingReq(true)
      const res = await fetch(`${API_BASE}/api/requestimages`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
          livescenarioid: liveId,
          room,
          requestmsg: requestmsg,
          }),
      });

      if (!res.ok) {
        throw new Error(`Request failed: ${res.status}`);
      }

      setRequestOpen(false);
      setModality(null);
      setStudyType(null);
    } catch (err) {
      console.error('Error requesting bloods:', err);
      alert('Failed to request bloods. Please try again.');
    } finally {
      setSavingReq(false);
    };
  };


  return (
    <Box sx={{ display: 'flex', gap: 2, height: '98%' }}>
      {/* Sidebar */}
      <Paper
        elevation={0}
        sx={{
          width: 280,
          minWidth: 230,
          borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`,
          bgcolor: 'white',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1.5,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Typography sx={{ fontSize: 16, fontWeight: 700 }}>
            Radiology / ECGs
          </Typography>
        </Box>

        <Box sx={{ px: 1.5, pt: 1, pb: 1 }}>
          <Chip
            label={`${sortedImaging.length} item${sortedImaging.length === 1 ? '' : 's'}`}
            size="small"
            sx={{ bgcolor: ACCENT, border: `1px solid ${ACCENT_BORDER}` }}
          />
        </Box>

        <Divider />

        <Box sx={{ flex: 1, overflowY: 'auto' }}>
          {loading && (
            <Box sx={{ p: 2 }}>
              {[...Array(4)].map((_, i) => (
                <Box key={i} sx={{ mb: 1.5 }}>
                  <Skeleton variant="rounded" height={20} />
                </Box>
              ))}
            </Box>
          )}

          {!loading && (
            <List dense disablePadding>
              {sortedImaging.map((img, index) => {
                const key = img.image_id || img.id || index;
                const selected =
                  (selectedImage?.image_id && selectedImage.image_id === img.image_id) ||
                  (selectedImage?.id && selectedImage.id === img.id);
                const dateVal = getImagingDate(img);

                return (
                  <ListItemButton
                    key={key}
                    onClick={() => setSelectedImage(img)}
                    selected={selected}
                    sx={{
                      mx: 1,
                      my: 0.5,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: selected ? ACCENT_BORDER : 'transparent',
                      bgcolor: selected ? ACCENT_SELECTED : 'transparent',
                      '&:hover': { bgcolor: ACCENT_HOVER },
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 34, color: ACCENT_BORDER }}>
                      <ImageOutlinedIcon fontSize="small" />
                    </ListItemIcon>

                    <ListItemText
                      primary={
                        <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                          {img.study_type || 'Untitled image'}
                        </Typography>
                      }
                      secondary={
                        <Stack spacing={0.25}>
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
                            {dateVal ? dayjs(dateVal).format('DD/MM/YYYY HH:mm') : 'No date'}
                          </Typography>
                        </Stack>
                      }
                    />
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Paper>

      {/* Viewer pane */}
      <Paper
        elevation={0}
        sx={{
          flex: 1,
          borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`,
          bgcolor: 'white',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Box
          sx={{
            px: 2,
            py: 1,
            bgcolor: ACCENT,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <Typography sx={{ fontWeight: 800, fontSize: 18 }}>
            {selectedImage ? (selectedImage.study_type || 'Image')  : 'No image selected'}
          </Typography>

          {selectedImage && (
            <Chip
              size="small"
              sx={{ bgcolor: 'white', border: `1px solid ${ACCENT_BORDER}` }}
              label={
                getImagingDate(selectedImage)
                  ? dayjs(getImagingDate(selectedImage)).format('DD/MM/YYYY HH:mm')
                  : 'Undated'
              }
            />
          )}

          <Button variant="contained" size="small"  sx={{ textTransform: 'none', boxShadow: 'none' }} onClick={() => setRequestOpen(true)}>Request</Button>
        </Box>

        <Box
          sx={{
            p: 3,
            flex: 1,
            overflowY: 'auto',
            bgcolor: '#F8FAFF',
          }}
        >
          {loading ? (
            <Stack spacing={2}>
              <Skeleton variant="rounded" height={28} />
              <Skeleton variant="rounded" height={300} />
              <Skeleton variant="rounded" height={18} />
              <Skeleton variant="rounded" height={18} />
            </Stack>
          ) : selectedImage ? (
            <Stack spacing={2.5}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid #dbe3f0',
                  bgcolor: 'white',
                  p: 2,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 360,
                }}
              >
                {selectedImage.imageUrl ? (
                  <Box
                    component="img"
                    src={selectedImage.imageUrl}
                    alt={selectedImage.title || 'Radiology image'}
                    sx={{
                      maxWidth: '100%',
                      maxHeight: '70vh',
                      objectFit: 'contain',
                      borderRadius: 1,
                    }}
                  />
                ) : (
                  <Typography color="text.secondary">
                    Image unavailable.
                  </Typography>
                )}
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  borderRadius: 2,
                  border: '1px solid #dbe3f0',
                  bgcolor: 'white',
                  p: 2,
                }}
              >
                <Typography sx={{ fontWeight: 700, mb: 1 }}>
                  Report
                </Typography>

                {selectedImage.report_visible ? (
                  selectedImage.report ? (
                    <Typography sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {selectedImage.report}
                    </Typography>
                  ) : (
                    <Typography color="text.secondary">
                      No report available.
                    </Typography>
                  )
                ) : (
                  <Typography color="text.secondary">
                    Report not visible.
                  </Typography>
                )}
              </Paper>
            </Stack>
          ) : (
            <Typography sx={{ color: 'text.secondary', textAlign: 'center', mt: 4 }}>
              Select an item from the left to view the image and report.
            </Typography>
          )}
        </Box>
      </Paper>

      {/* Request modal (same visual language as other pages) */}
      <Modal open={requestOpen} onClose={() => setRequestOpen(false)}>
        <Box sx={{
          position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
          width: 700, maxWidth: '95vw', bgcolor: 'white', borderRadius: 3,
          border: `1px solid ${ACCENT_BORDER}`, boxShadow: 24, overflow: 'hidden'
        }}>
          <Box sx={{
            px: 2, py: 1,
            backgroundColor: TOPBAR_BG,
            borderBottom: `1px solid ${ACCENT_BORDER}`,
            display: 'flex', alignItems: 'center', justifyContent: 'space-between'
          }}>
            <Typography sx={{ fontWeight: 800 }}>Request imaging</Typography>
            <Chip size="small" label="Participant" />
          </Box>

          <Box sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Autocomplete
          options={modalityOptions}
          value={modality}
          onChange={(event, newValue) => {
            setModality(newValue);
            setStudyType(null);
          }}
          renderInput={(params) => (
            <TextField {...params} label="Imaging modality" />
          )}
        />

        <Autocomplete
          options={studyTypeOptions}
          value={studyType}
          onChange={(event, newValue) => {
            setStudyType(newValue);
          }}
          disabled={!modality}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Study type"
              placeholder={modality ? 'Select study type' : 'Choose modality first'}
            />
          )}
        />
      </Stack>

          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 2, pb: 2 }}>
            <Button onClick={() => setRequestOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              onClick={sendRequest}
              disabled={savingReq || !modality || !studyType}
            >
              {savingReq ? 'Sending...' : 'Send request'}
            </Button>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
}